import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowLeft, AlertTriangle } from 'lucide-react';
import { FigIcon } from '../components/FigIcon';

const DisclaimerPage = () => {
  return (
    <div className="min-h-screen bg-[#E8E4DC]">
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#525252] hover:text-[#1A1C19]">
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
          <div className="flex items-center gap-2">
            <FigIcon className="w-6 h-6 text-[#2F3E30]" />
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <AlertTriangle className="w-8 h-8 text-[#F59E0B]" />
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Libre Baskerville, serif' }}>MEDICAL DISCLAIMER</h1>
          </div>

          <div className="p-6 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/30 mb-8">
            <p className="text-[#F59E0B] font-medium">
              This is an important document. Please read it carefully before using Trajectory.
            </p>
          </div>

          <div className="prose prose-invert prose-sm max-w-none space-y-6 text-[#525252]">
            <section>
              <h2 className="text-xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>NOT MEDICAL ADVICE</h2>
              <p>
                Trajectory is an educational platform designed to provide information about metabolic 
                health risk factors. <strong className="text-[#1A1C19]">The content, tools, and recommendations 
                provided by Trajectory do not constitute medical advice, diagnosis, or treatment.</strong>
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>EDUCATIONAL PURPOSE ONLY</h2>
              <p>
                The Metabolic Trajectory Score and associated recommendations are for educational and 
                informational purposes only. They are based on self-reported data and general research 
                about metabolic health risk factors.
              </p>
              <p>
                Our scoring algorithm cannot:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Diagnose any medical condition</li>
                <li>Predict individual health outcomes</li>
                <li>Replace professional medical evaluation</li>
                <li>Account for all individual health factors</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>SEEK PROFESSIONAL CARE</h2>
              <p>
                If you have any concerns about your health, or if you experience symptoms such as:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Unexplained weight changes</li>
                <li>Persistent fatigue</li>
                <li>Unusual thirst or urination</li>
                <li>Chest pain or shortness of breath</li>
                <li>Any other concerning symptoms</li>
              </ul>
              <p className="text-[#1A1C19] font-medium">
                Please consult a qualified healthcare provider immediately.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>LIMITATIONS</h2>
              <p>
                Our recommendations are general in nature and may not be appropriate for individuals with:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Existing medical conditions</li>
                <li>Medication requirements</li>
                <li>Specific dietary restrictions</li>
                <li>Physical limitations</li>
              </ul>
              <p>
                Always consult with your healthcare provider before making significant changes to your 
                diet, exercise routine, or lifestyle.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>NO WARRANTY</h2>
              <p>
                We make no warranties or representations about the accuracy, reliability, or completeness 
                of the information provided. Use of Trajectory is at your own risk.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-[#1A1C19]" style={{ fontFamily: 'Libre Baskerville, serif' }}>ACKNOWLEDGMENT</h2>
              <p>
                By using Trajectory, you acknowledge that you have read and understood this disclaimer, 
                and you agree that:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You will not rely on Trajectory as a substitute for professional medical advice</li>
                <li>You will consult healthcare providers for medical concerns</li>
                <li>You understand the limitations of health risk scoring tools</li>
                <li>You accept full responsibility for decisions made based on information from Trajectory</li>
              </ul>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DisclaimerPage;
